import { useAtomValue } from 'jotai';
import { Source } from 'react-map-gl';
import { Layer, LayerBand, LAYER_ORDER } from '../map/ordered-layer';
import { createMapSourceAtom } from '../map/atoms';
import { MapUserConfigAtom } from '../map/types';
import { useMemo, useEffect, useState } from 'react';

interface StructureGridLayerProps {
  mapConfigAtom: MapUserConfigAtom;
}

// Structure grid source configuration
interface StructureGridSource {
  id: string;
  url: string;
  formationId: string;
  sourceType: 'raster' | 'contour';
}

export const StructureGridLayer = ({
  mapConfigAtom,
}: StructureGridLayerProps) => {
  const mapConfig = useAtomValue(mapConfigAtom);
  const [sources, setSources] = useState<StructureGridSource[]>([]);

  const structureGridSurfaceSourceAtom = useMemo(
    () => createMapSourceAtom('structureGridSurfaces'),
    []
  );
  const structureGridSurfaceSource = useAtomValue(
    structureGridSurfaceSourceAtom
  );

  const structureGridContourSourceAtom = useMemo(
    () => createMapSourceAtom('structureGridContours'),
    []
  );
  const structureGridContourSource = useAtomValue(
    structureGridContourSourceAtom
  );

  useEffect(() => {
    if (
      (!structureGridSurfaceSource && !structureGridContourSource) ||
      !mapConfig.structureGrids?.checked
    ) {
      setSources([]);
      return;
    }

    const newSources: StructureGridSource[] = [];

    const structureGrids = mapConfig.structureGrids || {
      selections: {},
      options: {},
    };
    const selections = structureGrids.selections || {};

    Object.entries(selections).forEach(([basinId, formationId]) => {
      if (!formationId) return;

      const formationKey = `${basinId}-${formationId}`;
      const options = structureGrids.options?.[formationKey] || {};

      if (options.structure && structureGridSurfaceSource) {
        newSources.push({
          id: `structure-grid-raster-${formationId}`,
          url: `${structureGridSurfaceSource}?structure_grid_id=${formationId}&map=true`,
          formationId,
          sourceType: 'raster',
        });
      }

      if (options.contour && structureGridContourSource) {
        newSources.push({
          id: `structure-grid-contour-${formationId}`,
          url: `${structureGridContourSource}?structure_grid_id=${formationId}`,
          formationId,
          sourceType: 'contour',
        });
      }
    });

    setSources(newSources);
  }, [
    mapConfig.structureGrids,
    structureGridSurfaceSource,
    structureGridContourSource,
  ]);

  if (sources.length === 0) return null;

  return (
    <LayerBand band={LAYER_ORDER.structureGrids}>
      {sources.map((source) => (
        <div key={source.id}>
          {source.sourceType === 'raster' && (
            <Source id={source.id} type="vector" url={source.url}>
              <Layer
                id={`${source.id}-fill`}
                type="fill"
                source-layer="structure-grid-dynamic"
                paint={{
                  'fill-color': [
                    'interpolate',
                    ['linear'],
                    [
                      '/',
                      ['-', ['get', 'elevation'], ['get', 'min_val']],
                      ['-', ['get', 'max_val'], ['get', 'min_val']],
                    ],
                    0,
                    '#00ff00',
                    0.25,
                    '#7fff00',
                    0.5,
                    '#ffff00',
                    0.75,
                    '#ff7f00',
                    1,
                    '#ff0000',
                  ],
                  'fill-opacity': 0.7,
                  'fill-antialias': false,
                }}
              />
            </Source>
          )}

          {source.sourceType === 'contour' && (
            <Source id={source.id} type="vector" url={source.url}>
              <Layer
                id={`${source.id}-lines`}
                type="line"
                source-layer="structure-grid-contours"
                paint={{
                  'line-color': '#000000',
                  'line-width': [
                    'case',
                    ['==', ['%', ['abs', ['get', 'value']], 1000], 0],
                    2.5,
                    1,
                  ],
                  'line-opacity': 0.8,
                }}
              />
              <Layer
                id={`${source.id}-grid-symbols`}
                type="symbol"
                source-layer="structure-grid-contours"
                layout={{
                  'text-field': ['get', 'label'],
                  'text-font': ['Open Sans Regular'],
                  'text-size': 12,
                  'text-allow-overlap': false,
                  'symbol-placement': 'line',
                  'text-padding': 5,
                }}
                paint={{
                  'text-color': '#000000',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 2,
                }}
              />
            </Source>
          )}
        </div>
      ))}
    </LayerBand>
  );
};
